import React from 'react';
import { isReactComponent } from '../../../../utilities/ui';
import { shouldHideReviewFieldLabel } from './reviewPageContent';
/*
 * This is the template for each field (which in the schema library means label + widget)
 */

export default function ReviewFieldTemplate(props) {
  const { children, uiSchema, schema, formContext } = props;
  const uiOptions = uiSchema?.['ui:options'] || {};
  const reviewTitle = uiOptions.reviewTitle;
  const label =
    (typeof reviewTitle === 'string' && reviewTitle) ||
    uiSchema['ui:title'] ||
    props.label;
  const description = uiSchema['ui:description'];
  const textDescription = typeof description === 'string' ? description : null;
  const DescriptionField = isReactComponent(description)
    ? uiSchema['ui:description']
    : null;
  const pageIndex = formContext?.pagePerItemIndex;

  // `hideEmptyValueInReview` option is ignored if a 'ui:reviewField' is defined
  // The custom reviewField should handle empty values
  if (uiSchema?.['ui:reviewField']) {
    return uiSchema['ui:reviewField'](props);
  }

  if (schema.type === 'object' || schema.type === 'array') {
    return children;
  }

  let value = children;
  if (typeof children !== 'undefined') {
    if (children && typeof children === 'object' && 'props' in children) {
      value =
        children.props.formData !== undefined
          ? children.props.formData
          : children.props.value;
    } else if (
      children &&
      typeof children === 'object' &&
      'value' in children
    ) {
      value = children.value;
    }
  }
  const isEmptyValue =
    typeof value === 'undefined' || value === null || value === '';
  const hideEmpty = uiOptions.hideEmptyValueInReview;
  const optionalEmptyName = /^(middle|suffix|unit|addressLine2|addressLine3)$/i.test(
    props.name || '',
  );

  const renderOptions = formContext?.renderOptions;
  if (isEmptyValue && (hideEmpty || (renderOptions?.hideEmptyOptional && optionalEmptyName))) {
    return null;
  }
  const hideLabelOnReview = Boolean(
    formContext?.hideRedundantLabel ||
      shouldHideReviewFieldLabel(label, formContext?.pageTitle, uiOptions, {
        flatReview: formContext?.flatReview,
      }),
  );
  const hideFieldDescription = Boolean(renderOptions?.hideFieldDescription);
  const Tag = uiOptions.useDlWrap ? 'dl' : 'div';

  /**
   * Web components field handling
   *
   * For the review page, widgets are chosen based on their schema type
   * (boolean, string, etc) or if ui:widget is defined, then it will look
   * up a review widget based on that same name. See ./widgets.jsx or
   * ./StringField.jsx for examples.
   *
   * Easiest solution is to just leverage the widget logic and choose an
   * appropriate widget for the review page, or we can also choose to
   * set the ui:reviewField or define a ui:reviewWidget
   */
  if (
    uiSchema?.['ui:webComponentField'] &&
    !uiSchema?.['ui:widget'] &&
    !uiSchema?.['ui:reviewField']
  ) {
    if (uiSchema?.['ui:webComponentField'].name === 'VaMemorableDateField') {
      uiSchema['ui:widget'] = 'date';
    } else if (uiSchema?.['ui:webComponentField'].name === 'VaRadioField') {
      uiSchema['ui:widget'] = 'radio';
    }
  }

  return (
    <Tag
      className={
        hideLabelOnReview ? 'review-row review-row--value-only' : 'review-row'
      }
    >
      {hideLabelOnReview ? null : (
        <dt>
          {label}
          {!hideFieldDescription && textDescription && <p>{textDescription}</p>}
          {!hideFieldDescription && DescriptionField && (
            <DescriptionField
              options={uiSchema['ui:options']}
              index={pageIndex}
            />
          )}
          {!hideFieldDescription &&
            !textDescription &&
            !DescriptionField &&
            description}
        </dt>
      )}
      <dd>
        {renderOptions?.emptyRequiredLabel && isEmptyValue
          ? renderOptions.emptyRequiredLabel
          : children}
      </dd>
    </Tag>
  );
}
